import { PageWrapper } from '@components/page-wrapper/page-wrapper';
import { AppContextProvider } from '@context/app-context-provider';
import { type Config, defaultConfig } from '@default-config';
import { SkeletonThemeProvider } from '@eika/skeleton';
import { loadContainers } from '@eika-infrastruktur/render';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AccountForChildProductBulletsWidget } from '@widgets/account-for-child-product-bullets/account-for-child-product-bullets-widget';
import { GovernorAmountThresholdDetailsWidget } from '@widgets/governor-amount-threshold-details/governor-amount-threshold-details-widget';
import { HowToCreateInfoWidget } from '@widgets/how-to-create-info/how-to-create-info-widget';
import { OverviewOfSavingInfoWidget } from '@widgets/overview-of-saving-info/overview-of-saving-info-widget';
import { ChildAccountRoutesWidget } from '@widgets/router/child-account-routes-widget';
import { ScholarshipDetailsWidget } from '@widgets/scholarship-details/scholarship-details-widget';
import React from 'react';

const globalCssNamespace = 'caw'; // AKA: Child Account Web
export const REPO_NAMESPACE = 'sparing';

const NUM_OF_MINUTES = 5;
const staleTime = 60 * 60 * NUM_OF_MINUTES;
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime,
            retry: 1,
            refetchOnWindowFocus: false,
            refetchIntervalInBackground: false,
            retryOnMount: false,
        },
    },
});

const renderApp = (MyApp: React.ComponentType, namespace: string, appDefaultConfig: Config = defaultConfig): void => {
    loadContainers(
        `${REPO_NAMESPACE}.${namespace}`,
        () => (
            <React.StrictMode>
                <QueryClientProvider client={queryClient}>
                    <AppContextProvider>
                        <SkeletonThemeProvider>
                            <PageWrapper>
                                <div className={globalCssNamespace}>
                                    <MyApp />
                                </div>
                            </PageWrapper>
                        </SkeletonThemeProvider>
                    </AppContextProvider>
                </QueryClientProvider>
            </React.StrictMode>
        ),
        appDefaultConfig,
    );
};

const renderAppWithoutAppContext = (
    MyApp: React.ComponentType,
    namespace: string,
    appDefaultConfig: Config = defaultConfig,
): void => {
    loadContainers(
        `${REPO_NAMESPACE}.${namespace}`,
        () => (
            <React.StrictMode>
                <QueryClientProvider client={queryClient}>
                    <SkeletonThemeProvider>
                        <PageWrapper>
                            <div className={globalCssNamespace}>
                                <MyApp />
                            </div>
                        </PageWrapper>
                    </SkeletonThemeProvider>
                </QueryClientProvider>
            </React.StrictMode>
        ),
        appDefaultConfig,
    );
};

renderApp(ChildAccountRoutesWidget, 'child-account-web');
renderApp(HowToCreateInfoWidget, 'how-to-create-info-widget');
renderApp(OverviewOfSavingInfoWidget, 'overview-of-saving-info-widget');
renderAppWithoutAppContext(AccountForChildProductBulletsWidget, 'account-for-child-product-bullets-widget');
renderAppWithoutAppContext(ScholarshipDetailsWidget, 'scholarship-details-widget');
renderAppWithoutAppContext(GovernorAmountThresholdDetailsWidget, 'governor-amount-threshold-details-widget');
